import { Directive, ElementRef, Input, OnInit, OnDestroy, AfterViewInit, Renderer2, inject } from '@angular/core';

/**
 * Positioning strategy for menu placement.
 */
export type MenuPositionStrategy = 'right-preferred' | 'left-preferred' | 'auto';

/**
 * Positioning information for a menu element.
 */
export interface IMenuPosition {
  left: number;
  top: number;
  position: 'left' | 'right';
}

/**
 * Directive that handles intelligent positioning of dropdown/popup menus.
 * Provides right-preferred positioning with intelligent fallbacks based on available space.
 *
 * @example
 * ```html
 * <div nrcMenuPosition="right-preferred"
 *      [triggerElement]="buttonRef"
 *      [menuWidth]="192"
 *      [offset]="4">
 *   Menu content
 * </div>
 * ```
 */
@Directive({
  selector: '[nrcMenuPosition]',
  standalone: true
})
export class MenuPositionDirective implements OnInit, AfterViewInit, OnDestroy {
  @Input() public nrcMenuPosition: MenuPositionStrategy = 'right-preferred';
  @Input() public triggerElement!: HTMLElement;
  @Input() public menuWidth = 192; // Tailwind w-48
  @Input() public offset = 4;
  @Input() public minEdgeDistance = 10; // Minimum distance from viewport edges

  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);

  private resizeObserver?: ResizeObserver;
  private mutationObserver?: MutationObserver;
  private isPositioned = false;

  /**
   * Initialize the directive.
   */
  public ngOnInit(): void {
    this.setupInitialStyles();
  }

  /**
   * Set up positioning after view initialization.
   */
  public ngAfterViewInit(): void {
    if (this.triggerElement) {
      this.positionMenu();
      this.setupListeners();
    }
  }

  /**
   * Clean up resources when directive is destroyed.
   */
  public ngOnDestroy(): void {
    this.cleanup();
  }

  /**
   * Force reposition the menu (useful when trigger element changes).
   */
  public reposition(): void {
    if (this.triggerElement) {
      this.positionMenu();
    }
  }

  /**
   * Set up initial styles for the menu element.
   */
  private setupInitialStyles(): void {
    const menuElement = this.elementRef.nativeElement;

    // Use fixed positioning to escape container boundaries (important for modals)
    this.renderer.setStyle(menuElement, 'position', 'fixed');
    this.renderer.setStyle(menuElement, 'z-index', '9999');

    // Initially hide the menu to prevent flickering
    this.renderer.setStyle(menuElement, 'opacity', '0');
    this.renderer.setStyle(menuElement, 'visibility', 'hidden');
  }

  /**
   * Calculate and apply the optimal position for the menu.
   */
  private positionMenu(): void {
    if (!this.triggerElement) {
      return;
    }

    const menuElement = this.elementRef.nativeElement;
    const position = this.calculateOptimalPosition();
    const origin = this.getMenuOrigin();
    const adjustedLeft = position.left - origin.left;
    const adjustedTop = position.top - origin.top;

    // Apply the calculated position
    this.renderer.setStyle(menuElement, 'left', `${adjustedLeft}px`);
    this.renderer.setStyle(menuElement, 'top', `${adjustedTop}px`);

    // Add a CSS class to indicate the position side for styling purposes
    this.renderer.removeClass(menuElement, 'nrc-menu-left');
    this.renderer.removeClass(menuElement, 'nrc-menu-right');
    this.renderer.addClass(menuElement, `nrc-menu-${position.position}`);

    // Show the menu now that it's positioned
    if (!this.isPositioned) {
      this.renderer.setStyle(menuElement, 'opacity', '1');
      this.renderer.setStyle(menuElement, 'visibility', 'visible');
      this.isPositioned = true;
    }
  }

  /**
   * Calculate the optimal position for the menu based on available space.
   *
   * @returns
   */
  private calculateOptimalPosition(): IMenuPosition {
    if (!this.triggerElement) {
      return { left: 0, top: 0, position: 'right' };
    }

    const triggerRect = this.triggerElement.getBoundingClientRect();
    const menuElement = this.elementRef.nativeElement;
    const menuRect = menuElement.getBoundingClientRect();
    const menuHeight = menuRect.height || 240; // Fallback to max-height

    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate space available on each side
    const spaceRight = viewportWidth - triggerRect.right;
    const spaceLeft = triggerRect.left;

    // Determine horizontal position based on strategy
    let left: number;
    let position: 'left' | 'right';

    switch (this.nrcMenuPosition) {
      case 'right-preferred':
        if (spaceRight >= this.menuWidth + this.offset) {
          // Position to the right
          left = triggerRect.right + this.offset;
          position = 'right';
        } else if (spaceLeft >= this.menuWidth + this.offset) {
          // Fallback to left
          left = triggerRect.left - this.menuWidth - this.offset;
          position = 'left';
        } else {
          // Best fit positioning
          if (spaceRight >= spaceLeft) {
            left = Math.max(this.minEdgeDistance, viewportWidth - this.menuWidth - this.minEdgeDistance);
            position = 'right';
          } else {
            left = this.minEdgeDistance;
            position = 'left';
          }
        }
        break;

      case 'left-preferred':
        if (spaceLeft >= this.menuWidth + this.offset) {
          // Position to the left
          left = triggerRect.left - this.menuWidth - this.offset;
          position = 'left';
        } else if (spaceRight >= this.menuWidth + this.offset) {
          // Fallback to right
          left = triggerRect.right + this.offset;
          position = 'right';
        } else {
          // Best fit positioning
          if (spaceLeft >= spaceRight) {
            left = this.minEdgeDistance;
            position = 'left';
          } else {
            left = Math.max(this.minEdgeDistance, viewportWidth - this.menuWidth - this.minEdgeDistance);
            position = 'right';
          }
        }
        break;

      case 'auto':
      default:
        if (spaceRight >= spaceLeft && spaceRight >= this.menuWidth + this.offset) {
          left = triggerRect.right + this.offset;
          position = 'right';
        } else if (spaceLeft >= this.menuWidth + this.offset) {
          left = triggerRect.left - this.menuWidth - this.offset;
          position = 'left';
        } else {
          // Position based on which side has more space
          if (spaceRight >= spaceLeft) {
            left = Math.max(this.minEdgeDistance, viewportWidth - this.menuWidth - this.minEdgeDistance);
            position = 'right';
          } else {
            left = this.minEdgeDistance;
            position = 'left';
          }
        }
        break;
    }

    // Calculate vertical position
    let top = triggerRect.top;

    // Check if menu would overflow bottom of viewport
    if (top + menuHeight > viewportHeight - this.minEdgeDistance) {
      // Try positioning above the trigger
      const topAbove = triggerRect.bottom - menuHeight;

      if (topAbove >= this.minEdgeDistance) {
        top = topAbove;
      } else {
        // Position at the bottom edge of viewport with margin
        top = Math.max(this.minEdgeDistance, viewportHeight - menuHeight - this.minEdgeDistance);
      }
    }

    // Ensure menu doesn't go above viewport
    top = Math.max(this.minEdgeDistance, top);

    return { left, top, position };
  }

  /**
   * Get the menu element origin in viewport coordinates.
   *
   * @returns
   */
  private getMenuOrigin(): { left: number; top: number } {
    const menuElement = this.elementRef.nativeElement;

    this.renderer.setStyle(menuElement, 'left', '0px');
    this.renderer.setStyle(menuElement, 'top', '0px');

    const rect = menuElement.getBoundingClientRect();

    return { left: rect.left, top: rect.top };
  }

  /**
   * Set up event listeners for dynamic repositioning.
   */
  private setupListeners(): void {
    // ResizeObserver for viewport changes
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        this.positionMenu();
      });
      this.resizeObserver.observe(document.body);
    } else {
      // Fallback for browsers without ResizeObserver
      window.addEventListener('resize', this.handleReposition.bind(this));
    }

    // MutationObserver to detect DOM changes that might affect positioning
    if (window.MutationObserver) {
      this.mutationObserver = new MutationObserver(() => {
        this.positionMenu();
      });

      // Observe changes to the trigger element and its parents
      this.mutationObserver.observe(this.triggerElement, {
        attributes: true,
        attributeFilter: ['style', 'class']
      });
    }
  }

  /**
   * Handle repositioning (for fallback resize listener).
   */
  private handleReposition(): void {
    this.positionMenu();
  }

  /**
   * Clean up all event listeners and observers.
   */
  private cleanup(): void {
    // Clean up ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }

    window.removeEventListener('resize', this.handleReposition.bind(this));

    // Clean up MutationObserver
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = undefined;
    }
  }
}
